'use client';

import { cva } from 'class-variance-authority';
import { Cpu } from 'lucide-react';
import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { ModelPicker } from '@/components/ui/model-picker';
import { canonicalizeModelId, DEFAULT_MODEL_ID, FALLBACK_MODEL_ID, getModelOptions } from '@/constants/ai';
import { toModelPickerOption } from '@/lib/ai/model-picker';
import { cn } from '@/lib/utils';
import type { ModelPickerOption } from '@/types/ai';

interface ModelSelectorProperties {
  availableModels?: ModelPickerOption[];
  className?: string;
  disabled?: boolean;
  hasModelSelection?: boolean;
  helperText?: string | null;
  isAdmin?: boolean;
  labelText?: string;
  onChange: (value: string) => void;
  showIcon?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'default' | 'lg';
  value: string;
}

const MODEL_DEFINITIONS = getModelOptions();

const ADMIN_FALLBACK_MODELS: ModelPickerOption[] = MODEL_DEFINITIONS.map((model) =>
  toModelPickerOption(model.value)
).filter((option): option is ModelPickerOption => option !== null);

const USER_SELECTABLE_CANONICAL_IDS = new Set([DEFAULT_MODEL_ID, FALLBACK_MODEL_ID]);

const USER_FALLBACK_MODELS: ModelPickerOption[] = MODEL_DEFINITIONS.filter((model) =>
  USER_SELECTABLE_CANONICAL_IDS.has(model.value)
)
  .map((model) => {
    const option = toModelPickerOption(model.value);
    if (!option) {
      return null;
    }
    const suffix = model.value === DEFAULT_MODEL_ID ? ' (Premium)' : ' (Standard)';
    const display = `${option.label}${suffix}`;
    return {
      ...option,
      displayName: display,
      label: display,
    } satisfies ModelPickerOption;
  })
  .filter((option): option is ModelPickerOption => option !== null);

const getFallbackModels = (
  availableModels: ModelPickerOption[] | undefined,
  isAdmin: boolean,
  hasModelSelection: boolean
): ModelPickerOption[] => {
  if (availableModels && availableModels.length > 0) {
    return availableModels;
  }
  if (isAdmin) {
    return ADMIN_FALLBACK_MODELS;
  }
  if (hasModelSelection) {
    return USER_FALLBACK_MODELS;
  }
  return [];
};

const sizeToTriggerClass: Record<NonNullable<ModelSelectorProperties['size']>, string> = {
  sm: 'h-10 text-sm',
  default: 'h-11 text-sm',
  lg: 'h-12 text-base',
};

const labelVariants = cva('flex items-center gap-2 font-medium', {
  variants: {
    size: {
      sm: 'text-sm',
      default: 'text-base',
      lg: 'text-base',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const iconVariants = cva('', {
  variants: {
    size: {
      sm: 'h-3 w-3',
      default: 'h-4 w-4',
      lg: 'h-4 w-4',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export function ModelSelector({
  value,
  onChange,
  disabled = false,
  availableModels,
  isAdmin = false,
  hasModelSelection = false,
  className = '',
  showIcon = true,
  showLabel = true,
  labelText = 'AI Model',
  size = 'default',
  helperText: helperTextProperty,
}: ModelSelectorProperties) {
  const models = useMemo(
    () => getFallbackModels(availableModels, isAdmin, hasModelSelection),
    [availableModels, isAdmin, hasModelSelection]
  );

  if (models.length === 0) {
    return null;
  }

  const triggerClassName = sizeToTriggerClass[size] ?? sizeToTriggerClass.default;
  let helperText: string | undefined;
  if (helperTextProperty !== undefined) {
    helperText = helperTextProperty ?? undefined;
  } else if (!isAdmin) {
    helperText = 'Choose between premium and standard models for your analysis';
  }

  const trimmedValue = value.trim();
  const canonicalValue = canonicalizeModelId(trimmedValue);
  const pickerValue = (() => {
    if (canonicalValue && canonicalValue.length > 0) {
      return canonicalValue;
    }
    if (trimmedValue.length > 0) {
      return trimmedValue;
    }
    return null;
  })();

  return (
    <div className={cn('space-y-3', className)}>
      {showLabel ? (
        <Label className={labelVariants({ size })}>
          {showIcon ? <Cpu className={iconVariants({ size })} /> : null}
          {labelText}
        </Label>
      ) : null}
      <ModelPicker
        options={models}
        value={pickerValue}
        onChange={onChange}
        disabled={disabled}
        placeholder="Select a model"
        helperText={helperText}
        showTweetLimit
        showContextTokens
        showProvider
        showTier
        triggerClassName={triggerClassName}
        getOptionValue={(option) => option.canonicalId}
        getDisplayLabel={(option) => option.label ?? option.displayName}
      />
    </div>
  );
}
